"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useId, useState } from "react";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import type { IAccordionListProps } from "./AccordionList.props";

export const AccordionList = ({ items, openBehavior }: IAccordionListProps) => {
  const firstOpen = items.findIndex((item) => item.initiallyOpen);
  const [openItems, setOpenItems] = useState<number[]>(
    openBehavior === "multiple-open"
      ? items.flatMap((item, index) => (item.initiallyOpen ? [index] : []))
      : firstOpen >= 0
        ? [firstOpen]
        : [],
  );
  const baseId = useId();
  const toggle = (index: number) =>
    setOpenItems((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : openBehavior === "multiple-open"
          ? [...current, index]
          : [index],
    );

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => {
        const open = openItems.includes(index);
        const triggerId = `${baseId}-trigger-${index}`;
        const panelId = `${baseId}-panel-${index}`;
        return (
          <div className="rounded-[20px] bg-daylight px-5 md:px-6" key={index}>
            <h3>
              <button
                aria-controls={panelId}
                aria-expanded={open}
                className="flex min-h-16 w-full cursor-pointer items-center justify-between gap-5 py-5 text-left font-daylight-display text-xl font-semibold transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                id={triggerId}
                onClick={() => toggle(index)}
                type="button"
              >
                <span>{item.title}</span>
                <Icon
                  className={cn(
                    "size-9 shrink-0 rounded-full bg-sky p-2 text-cobalt transition-transform duration-200",
                    open && "rotate-45 bg-cobalt text-daylight",
                  )}
                  name="plus"
                />
              </button>
            </h3>
            <div
              aria-labelledby={triggerId}
              className="grid"
              hidden={!open}
              id={panelId}
              role="region"
            >
              <DaylightRichText
                className="max-w-[65ch] pb-6 text-base text-basalt md:text-lg"
                content={item.content}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

AccordionList.displayName = "Content::AccordionList";
